import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Building2, MapPin, BriefcaseBusiness, Banknote, Flag } from "lucide-react";
import PlanBadge from "../components/PlanBadge";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetail, setReportDetail] = useState("");
  const [reportDone, setReportDone] = useState(false);

  const submitReport = async () => {
    try {
      await api.post("/reports", { target_type: "job", target_id: id, reason: reportReason, detail: reportDetail });
      setReportDone(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit report");
    }
  };

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-16 font-display text-2xl">Loading…</div>;
  if (err || !job) return <div className="p-16">{err || "Not found"}</div>;

  const canApply = user && user.role === "candidate";

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/jobs" className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> All roles
      </Link>

      <div className="mt-8 border-b border-[#E2E0D8] pb-10">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="label-eyebrow">{job.company}</div>
          <PlanBadge plan={job.recruiter_plan} size="md" />
        </div>
        <h1 className="font-display font-black text-5xl lg:text-6xl mt-3 tracking-tight">{job.title}</h1>
        {job.org_type === "game" && (
          <div data-testid="job-game-disclaimer" className="mt-5 border border-purple-300 bg-purple-50 px-4 py-3 text-sm text-purple-900">
            <strong className="font-display">Game group listing.</strong> This recruiter has self-identified as an online community / game. <strong>Not affiliated with any official organisation.</strong> Apply only if you understand the context — this is not a real paid vacancy.
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[var(--brand-primary)]" />{job.company}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[var(--brand-primary)]" />{job.location}</span>
          <span className="inline-flex items-center gap-1.5"><BriefcaseBusiness className="w-4 h-4 text-[var(--brand-primary)]" />{job.employment_type}</span>
          {job.department && (
            <span className="inline-flex items-center gap-1.5 text-[#5C5C5C]">· {job.department}</span>
          )}
          {job.is_volunteer ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1.5 py-0.5" data-testid="volunteer-badge">Volunteer</span>
          ) : (job.salary_range && (
            <span className="inline-flex items-center gap-1.5"><Banknote className="w-4 h-4 text-[var(--brand-primary)]" />{job.salary_range}</span>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3 items-center">
          {!user && (
            <Link to="/login" state={{ from: location }} data-testid="apply-login" className="btn-terra">Sign in to apply</Link>
          )}
          {canApply && (
            <button data-testid="apply-button" onClick={() => navigate(`/jobs/${job.job_id}/apply`)} className="btn-terra">Apply for this role</button>
          )}
          {user && user.role !== "candidate" && (
            <span className="text-sm text-[#5C5C5C] italic">Switch to a candidate account to apply.</span>
          )}
          {user && (
            <button onClick={() => setReportOpen(true)} data-testid="report-job" className="ml-auto text-sm font-semibold text-[#5C5C5C] hover:text-[var(--brand-primary)] inline-flex items-center gap-1">
              <Flag className="w-3.5 h-3.5" /> Report this role
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-10 mt-10">
        <div className="col-span-12 lg:col-span-8 space-y-10">
          <section>
            <div className="label-eyebrow">About the role</div>
            <p className="mt-3 text-lg leading-relaxed whitespace-pre-line">{job.description}</p>
          </section>

          {job.responsibilities?.length > 0 && (
            <section>
              <div className="label-eyebrow">What you'll do</div>
              <ul className="mt-3 space-y-2">
                {job.responsibilities.map((r, i) => (
                  <li key={`resp-${i}-${r.slice(0, 20)}`} className="flex gap-3"><span className="text-[var(--brand-primary)] font-bold">—</span><span>{r}</span></li>
                ))}
              </ul>
            </section>
          )}

          {job.requirements?.length > 0 && (
            <section>
              <div className="label-eyebrow">What we expect</div>
              <ul className="mt-3 space-y-2">
                {job.requirements.map((r, i) => (
                  <li key={`req-${i}-${r.slice(0, 20)}`} className="flex gap-3"><span className="text-[var(--brand-primary)] font-bold">—</span><span>{r}</span></li>
                ))}
              </ul>
            </section>
          )}

          {job.benefits?.length > 0 && (
            <section>
              <div className="label-eyebrow">What we offer</div>
              <ul className="mt-3 space-y-2">
                {job.benefits.map((r, i) => (
                  <li key={`ben-${i}-${r.slice(0, 20)}`} className="flex gap-3"><span className="text-[var(--brand-primary)] font-bold">—</span><span>{r}</span></li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="col-span-12 lg:col-span-4">
          <div className="border border-[#0F0F0F] p-6 sticky top-24">
            <div className="label-eyebrow">Application form</div>
            <p className="mt-2 text-sm leading-relaxed">
              This role has <strong>{job.questions?.length || 0}</strong> custom question{(job.questions?.length || 0) === 1 ? "" : "s"} plus your resume.
            </p>
            <div className="mt-4 border-t border-[#E2E0D8] pt-4 text-sm space-y-2">
              {(job.questions || []).map((q) => (
                <div key={q.id} className="flex justify-between gap-3">
                  <span className="truncate">{q.label}</span>
                  <span className="text-[#5C5C5C] uppercase text-xs">{q.type}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" data-testid="report-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-7 max-w-md w-full">
            {!reportDone ? (
              <>
                <div className="label-eyebrow">Report this role</div>
                <h3 className="font-display font-black text-2xl mt-2">What's wrong?</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">Reports are reviewed by the owner within 48 hours.</p>
                <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-reason"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 mt-4">
                  <option value="">Select a reason…</option>
                  <option value="spam">Spam or duplicate</option>
                  <option value="misleading">Misleading or inaccurate</option>
                  <option value="discriminatory">Discriminatory language</option>
                  <option value="scam">Scam or fake company</option>
                  <option value="inappropriate">Inappropriate content</option>
                  <option value="other">Other</option>
                </select>
                <textarea value={reportDetail} onChange={(e) => setReportDetail(e.target.value)} rows={3} data-testid="report-detail"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 mt-3" placeholder="Optional detail…" />
                <div className="flex gap-2 mt-4">
                  <button onClick={submitReport} disabled={!reportReason} data-testid="report-submit" className="btn-terra flex-1 disabled:opacity-50">Submit report</button>
                  <button onClick={() => setReportOpen(false)} className="btn-ghost">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="label-eyebrow">Thank you</div>
                <h3 className="font-display font-black text-2xl mt-2">Report received.</h3>
                <p className="text-sm text-[#5C5C5C] mt-2">We'll review it and take action if needed.</p>
                <button onClick={() => { setReportOpen(false); setReportDone(false); setReportReason(""); setReportDetail(""); }}
                  className="btn-ink mt-4">Close</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
